import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SplitPaymentFields from '../common/SplitPaymentFields';
import { fieldLabelSx } from '../common/formFieldStyles';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import {
  createDefaultSplitPaymentLines,
  toCheckoutPayload,
  toSplitAmount,
  validateSplitPaymentLines,
} from '../../utils/splitPaymentUtils';
import { BRAND } from '../../theme/brandTokens';

const MembershipPeriodPaymentDialog = ({
  open,
  onClose,
  onConfirm,
  confirming = false,
  title,
  description,
  confirmText,
  amount = 0,
  currencyCode = DEFAULT_CURRENCY,
}) => {
  const { t } = useTranslation();
  const amountDue = Math.max(0, toSplitAmount(amount));
  const [lines, setLines] = useState(() => createDefaultSplitPaymentLines(amountDue));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      return;
    }

    setLines(createDefaultSplitPaymentLines(amountDue));
    setErrors({});
  }, [open, amountDue]);

  const handleConfirm = () => {
    const result = validateSplitPaymentLines({
      lines,
      amountDue,
      allowDiscount: false,
    });

    setErrors(result.errors);
    if (!result.isValid) {
      return;
    }

    onConfirm(toCheckoutPayload({
      payments: result.payments,
      collected: result.collected,
      amountDue,
    }));
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={confirming ? undefined : onClose}
      maxWidth="md"
      title={title}
      subtitle={description}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} disabled={confirming} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleConfirm} disabled={confirming}>
            {confirming ? t('common.saving') : confirmText}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>
            {t('customerMembershipsManagement.markPaid.amount')}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.accentText }}>
            {formatCurrency(amountDue, currencyCode)}
          </Typography>
        </Box>

        <SplitPaymentFields
          lines={lines}
          onChange={setLines}
          amountDue={amountDue}
          currencyCode={currencyCode}
          allowDiscount={false}
          disabled={confirming}
          errors={errors}
        />
      </Box>
    </ScrollableDialog>
  );
};

export default MembershipPeriodPaymentDialog;
